import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles, Box } from '@material-ui/core';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import Typography from '@material-ui/core/Typography';
import { Skeleton } from '@material-ui/lab';
import { currency } from 'Helpers/Formats';
import DonutChart from 'Components/UI/DonutChart';
import SelectControl from 'Components/UI/SelectControl';
import { refreshAccounts, isLiability } from 'Components/UI/Snapshot/helpers';
import { useRefreshAccounts, dispatchAccountsRefresh } from 'Hooks/useRefreshEvent';

const useStyles = makeStyles(theme => ({
  total: {
    color: theme.palette.facet.primaryBlue,
  },
  donutWrapper: {
    minWidth: 500,
    height: '100%',
    width: '100%',
    fontSize: 10,
  },
}));

const INVESTMENTS = 'Investment Assets';
const LIABILITIES = 'Liabilities';
const OWNERSHIP = 'Ownership';
const chartOptions = [
  { name: INVESTMENTS, value: INVESTMENTS },
  { name: LIABILITIES, value: LIABILITIES },
  { name: OWNERSHIP, value: OWNERSHIP },
];

export default function BalanceSheetChart() {
  const classes = useStyles();

  const { householdResource } = useSimpleHousehold();
  const [chartData, setChartData] = useState([]);
  const [selectedChartType, setSelectedChartType] = useState(INVESTMENTS);
  const [liabilities, setLiabilities] = useState([]);
  const [investmentAssets, setInvestmentAssets] = useState([]);
  const [realAssets, setRealAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);


  const calculateOwnershipChartData = (resources) => {
    const dataMap = {};
    const data = [];
    resources.forEach((resource) => {
      if (!resource.attr('owner')) {
        resource.attr('owner', 'No Owner');
      }
      dataMap[resource.attr('owner')] = dataMap[resource.attr('owner')]
        ? dataMap[resource.attr('owner')]
          + Math.abs(parseFloat(resource.attr('balance') || 0))
        : Math.abs(parseFloat(resource.attr('balance') || 0));
    });

    Object.keys(dataMap).forEach((key) => {
      data.push({ name: key, value: dataMap[key] });
    });
    return data;
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    const accounts = await refreshAccounts(householdResource);
    setLoading(false);
    if (accounts) {
      const { liabilities: l, investmentAssets: ia, realAssets: ra } = accounts;
      setLiabilities(l);
      setInvestmentAssets(ia);
      setRealAssets(ra);
    }
  }, [householdResource]);

  const calculateChartData = () => {
    const dataMap = {};
    const data = [];
    let resources = [];
    switch (selectedChartType) {
      case INVESTMENTS:
        resources = [...investmentAssets];
        break;
      case LIABILITIES:
        resources = [...liabilities];
        break;
      case OWNERSHIP:
        setChartData(
          calculateOwnershipChartData(
            [...liabilities, ...investmentAssets, ...realAssets],
            true,
          ),
        );
        return;
      default:
        resources = [];
        break;
    }
    resources.forEach((resource) => {
      if (isLiability(resource)) {
        dataMap[
          resource
            .meta()
            .field('type')
            .selectedOption()
            .attr('prompt')
        ] = dataMap[
          resource
            .meta()
            .field('type')
            .selectedOption()
            .attr('prompt')
        ]
          ? dataMap[
            resource
              .meta()
              .field('type')
              .selectedOption()
              .attr('prompt')
          ] + Math.abs(parseFloat(resource.attr('balance') || 0))
          : Math.abs(parseFloat(resource.attr('balance') || 0));
      } else if (resource.attr('type') === 'realAsset') {
        dataMap[resource.attr('accountType')] = dataMap[
          resource.attr('accountType')
        ]
          ? dataMap[resource.attr('accountType')]
            + parseFloat(resource.attr('balance') || 0)
          : parseFloat(resource.attr('balance') || 0);
      } else {
        dataMap[
          resource
            .meta()
            .field('accountType')
            .selectedOption()
            .attr('prompt')
        ] = dataMap[
          resource
            .meta()
            .field('accountType')
            .selectedOption()
            .attr('prompt')
        ]
          ? dataMap[
            resource
              .meta()
              .field('accountType')
              .selectedOption()
              .attr('prompt')
          ] + parseFloat(resource.attr('balance') || 0)
          : parseFloat(resource.attr('balance') || 0);
      }
    });

    Object.keys(dataMap).forEach((key) => {
      data.push({ name: key, value: dataMap[key] });
    });
    setChartData(data);
  };

  useEffect(() => {
    calculateChartData();
  }, [selectedChartType, liabilities, investmentAssets, realAssets]);

  useEffect(() => {
    let tot = 0;
    chartData.forEach((d) => {
      tot += parseFloat(d.value || 0);
    });
    setTotal(tot);
  }, [chartData]);

  useRefreshAccounts(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    dispatchAccountsRefresh();
  }, [householdResource]);

  return (
    <>
      <SelectControl
        className={classes.chartSelectContainer}
        value={selectedChartType || ''}
        onChange={(evt) => {
          setSelectedChartType(evt.target.value);
        }}
        options={chartOptions}
        label="Chart Type"
      />
      {loading
        ? <Skeleton animation="wave" variant="rect" width={654} height={300} className={classes.chart} />
        : (
          <>
            <Typography variant="h4" className={classes.total}>
              {`${selectedChartType} Total: ${currency(total)}`}
            </Typography>
            <Box className={classes.donutWrapper}>
              <DonutChart
                data={chartData}
                height={100}
                animationBegin={800}
                animationDuration={1200}
              />
            </Box>
          </>
        )
        }
    </>
  );
}
